import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControlLabel,
  Checkbox,
  TextField,
  Typography,
  Alert,
  Chip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Description as FileIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL, dateTimeFormatter } from '../configuration/Configuration';

const TenderDocuments = ({ tenderId, username, userType }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [expandedParents, setExpandedParents] = useState({});
  const [uploadParent, setUploadParent] = useState(null);
  const [requiresReupload, setRequiresReupload] = useState(false);

  const canUploadDocuments = Boolean(userType && (userType.isAdmin || userType.isStaff));
  const canDeleteDocuments = Boolean(userType && userType.isAdmin);
  const canUploadReuploads = Boolean(userType && !userType.isAdmin);

  const loadDocuments = useCallback(async () => {
    if (!tenderId) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/tender/${tenderId}/documents/`
      );
      setDocuments(response.data);
    } catch (err) {
      console.error('Error loading documents:', err);
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [tenderId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const selectFiles = (files) => {
    if (files.length > 0) {
      // Check file size (max 50MB per file)
      const maxSize = 50 * 1024 * 1024;
      const invalidFiles = files.filter(file => file.size > maxSize);
      
      if (invalidFiles.length > 0) {
        setError(`${invalidFiles.length} file(s) exceed 50MB limit`);
        return;
      }
      
      setSelectedFiles(files);
      setError(null);
    }
  };

  const handleFileSelect = (event) => {
    selectFiles(Array.from(event.target.files));
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = uploading ? 'none' : 'copy';
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    if (!uploading) {
      setIsDraggingFiles(true);
    }
  };

  const handleDragLeave = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsDraggingFiles(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDraggingFiles(false);

    if (!uploading) {
      selectFiles(Array.from(event.dataTransfer.files));
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one file');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);
    setUploadProgress({ current: 0, total: selectedFiles.length });

    const errors = [];
    let successCount = 0;

    // Upload files sequentially
    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      setUploadProgress({ current: i + 1, total: selectedFiles.length });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', description);
      formData.append('uploaded_by', username);
      if (uploadParent) {
        formData.append('parent_document', uploadParent.id);
      }
      if (!uploadParent) {
        formData.append('requires_reupload', requiresReupload);
      }

      try {
        await axios.post(
          `${API_BASE_URL}/api/tender/${tenderId}/documents/`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        successCount++;
      } catch (err) {
        console.error(`Error uploading ${file.name}:`, err);
        errors.push(`${file.name}: ${err.response?.data?.error || 'Failed to upload'}`);
      }
    }

    setUploading(false);
    setUploadProgress({ current: 0, total: 0 });

    if (successCount > 0) {
      setSuccess(`${successCount} document(s) uploaded successfully`);
      setUploadDialogOpen(false);
      setSelectedFiles([]);
      setDescription('');
      setUploadParent(null);
      setRequiresReupload(false);
      loadDocuments();
      window.dispatchEvent(
        new CustomEvent('tender-documents-updated', { detail: { tenderId } })
      );
    }

    if (errors.length > 0) {
      setError(`Failed to upload ${errors.length} file(s): ${errors.join('; ')}`);
    }
  };

  const handleDelete = async (documentId, filename) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${filename}"?`
      )
    ) {
      return;
    }

    try {
      await axios.delete(
        `${API_BASE_URL}/api/tender/${tenderId}/documents/${documentId}/`
      );
      setSuccess('Document deleted successfully');
      loadDocuments();
      window.dispatchEvent(
        new CustomEvent('tender-documents-updated', { detail: { tenderId } })
      );
    } catch (err) {
      console.error('Error deleting document:', err);
      setError('Failed to delete document');
    }
  };

  const handleDownload = async (documentId, filename) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/tender/${tenderId}/documents/${documentId}/download/`
      );
      
      // Open the download URL in a new window
      window.open(response.data.url, '_blank');
    } catch (err) {
      console.error('Error downloading document:', err);
      setError('Failed to download document');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDate = (dateString) => {
    return dateTimeFormatter({ value: dateString });
  };

  const parentDocuments = documents.filter((doc) => !doc.parent_document);
  const childDocumentsByParent = documents.reduce((acc, doc) => {
    if (doc.parent_document) {
      if (!acc[doc.parent_document]) {
        acc[doc.parent_document] = [];
      }
      acc[doc.parent_document].push(doc);
    }
    return acc;
  }, {});

  const toggleParent = (parentId) => {
    setExpandedParents((prev) => ({
      ...prev,
      [parentId]: !prev[parentId],
    }));
  };

  const openUploadDialog = (parentDoc = null) => {
    setUploadParent(parentDoc);
    setRequiresReupload(false);
    setSelectedFiles([]);
    setDescription('');
    setIsDraggingFiles(false);
    setUploadDialogOpen(true);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6">Documents</Typography>
        {canUploadDocuments && (
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => openUploadDialog()}
            sx={{
              backgroundColor: 'common.white',
              color: 'text.primary',
              borderColor: 'divider',
              '&:hover': {
                backgroundColor: 'grey.50',
                borderColor: 'text.secondary',
              },
            }}
          >
            Upload Document
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : parentDocuments.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="textSecondary" align="center">
              No documents uploaded yet
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <List>
          {parentDocuments.map((doc) => {
            const childDocs = childDocumentsByParent[doc.id] || [];
            const hasChildren = childDocs.length > 0;
            const isExpanded = Boolean(expandedParents[doc.id]);

            return (
              <Card key={doc.id} sx={{ mb: 1 }}>
                <ListItem>
                  <FileIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            cursor: 'pointer',
                            color: 'primary.main',
                            textDecoration: 'underline',
                            '&:hover': {
                              color: 'primary.dark',
                            },
                          }}
                          onClick={() => handleDownload(doc.id, doc.original_filename)}
                        >
                          {doc.original_filename}
                        </Typography>
                        <Chip
                          label={formatFileSize(doc.file_size)}
                          size="small"
                          variant="outlined"
                        />
                        {doc.requires_reupload && (
                          <Chip
                            label="Requires Reupload"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        {doc.description && (
                          <Typography variant="body2" color="textSecondary">
                            {doc.description}
                          </Typography>
                        )}
                      <Typography variant="caption" color="textSecondary">
                        Uploaded by {doc.uploaded_by_username || 'Unknown'}
                        {doc.uploaded_by_department
                          ? ` (${doc.uploaded_by_department})`
                          : ''}{' '}
                        on {formatDate(doc.uploaded_at)}
                      </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="download"
                      onClick={() => handleDownload(doc.id, doc.original_filename)}
                      sx={{ mr: 1 }}
                    >
                      <DownloadIcon />
                    </IconButton>
                    {canDeleteDocuments && (
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleDelete(doc.id, doc.original_filename)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>

                {(doc.requires_reupload || hasChildren) && (
                  <Box sx={{ px: 2, pb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        onClick={() => toggleParent(doc.id)}
                        startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      >
                        Reuploads ({childDocs.length})
                      </Button>
                      {canUploadReuploads && doc.requires_reupload && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openUploadDialog(doc)}
                        >
                          Upload Filled Version
                        </Button>
                      )}
                    </Box>

                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      {childDocs.length === 0 ? (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                          No reuploads yet
                        </Typography>
                      ) : (
                        <List dense sx={{ mt: 1 }}>
                          {childDocs.map((child) => (
                            <ListItem key={child.id} sx={{ pl: 2 }}>
                              <FileIcon sx={{ mr: 2, color: 'text.secondary' }} />
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        cursor: 'pointer',
                                        color: 'primary.main',
                                        textDecoration: 'underline',
                                        '&:hover': {
                                          color: 'primary.dark',
                                        },
                                      }}
                                      onClick={() =>
                                        handleDownload(child.id, child.original_filename)
                                      }
                                    >
                                      {child.original_filename}
                                    </Typography>
                                    <Chip
                                      label={formatFileSize(child.file_size)}
                                      size="small"
                                      variant="outlined"
                                    />
                                  </Box>
                                }
                                secondary={
                                  <Typography variant="caption" color="textSecondary">
                                    Uploaded by {child.uploaded_by_username || 'Unknown'}
                                    {child.uploaded_by_department
                                      ? ` (${child.uploaded_by_department})`
                                      : ''}{' '}
                                    on {formatDate(child.uploaded_at)}
                                  </Typography>
                                }
                              />
                              <ListItemSecondaryAction>
                                <IconButton
                                  edge="end"
                                  aria-label="download"
                                  onClick={() =>
                                    handleDownload(child.id, child.original_filename)
                                  }
                                  sx={{ mr: 1 }}
                                >
                                  <DownloadIcon />
                                </IconButton>
                                {canDeleteDocuments && (
                                  <IconButton
                                    edge="end"
                                    aria-label="delete"
                                    onClick={() =>
                                      handleDelete(child.id, child.original_filename)
                                    }
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                )}
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </Collapse>
                  </Box>
                )}
              </Card>
            );
          })}
        </List>
      )}

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => {
          if (!uploading) {
            setUploadDialogOpen(false);
            setUploadParent(null);
            setRequiresReupload(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {uploadParent && (
              <Alert severity="info">
                Uploading filled version for: {uploadParent.original_filename}
              </Alert>
            )}
            <Box
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                border: '2px dashed',
                borderColor: isDraggingFiles ? 'primary.main' : 'divider',
                backgroundColor: isDraggingFiles ? 'action.hover' : 'transparent',
                borderRadius: 1,
                p: 2,
                textAlign: 'center',
                transition: 'border-color 150ms ease, background-color 150ms ease',
              }}
            >
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                disabled={uploading}
              >
                {selectedFiles.length > 0
                  ? `${selectedFiles.length} file(s) selected`
                  : 'Choose Files'}
                <input
                  type="file"
                  multiple
                  hidden
                  onChange={handleFileSelect}
                  disabled={uploading}
                />
              </Button>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                {uploading
                  ? 'Files cannot be changed while uploading'
                  : isDraggingFiles
                    ? 'Drop files to select them'
                    : 'or drop files here'}
              </Typography>
            </Box>

            {selectedFiles.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Selected Files:
                </Typography>
                <List dense>
                  {selectedFiles.map((file, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={file.name}
                        secondary={formatFileSize(file.size)}
                      />
                      {!uploading && (
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => {
                            setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </ListItem>
                  ))}
                </List>
                <Typography variant="body2" color="textSecondary">
                  Total: {formatFileSize(selectedFiles.reduce((sum, f) => sum + f.size, 0))}
                </Typography>
              </Box>
            )}

            <TextField
              label="Description (Optional)"
              helperText="This description will be applied to all selected files"
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={uploading}
              fullWidth
            />

            {!uploadParent && canDeleteDocuments && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={requiresReupload}
                    onChange={(e) => setRequiresReupload(e.target.checked)}
                    disabled={uploading}
                  />
                }
                label="Require department users to reupload a filled version"
              />
            )}

            {uploading && uploadProgress.total > 0 && (
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Uploading {uploadProgress.current} of {uploadProgress.total}...
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Box sx={{ width: '100%', mr: 1 }}>
                    <CircularProgress
                      variant="determinate"
                      value={(uploadProgress.current / uploadProgress.total) * 100}
                    />
                  </Box>
                </Box>
              </Box>
            )}

            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setUploadDialogOpen(false);
              setUploadParent(null);
              setRequiresReupload(false);
            }}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={selectedFiles.length === 0 || uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
          >
            {uploading
              ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...`
              : `Upload ${selectedFiles.length > 0 ? `(${selectedFiles.length})` : ''}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenderDocuments;
